import React from 'react';
import DateInput, { type IDateInput } from './DateInput/DateInput';
import styles from './DatePicker.module.scss';
import blurHandler from '../../helpers/blur-handler';
import ValidationMessage from '../ValidationMessage/ValidationMessage';
import AssistiveText from '../AssistiveText/AssistiveText';

export interface IDatePicker {
  id: string;
  label: string;
  placeholders?: [string, string, string];
  subLabels?: [string, string, string];
  onChange: (value: IDate) => void;
  onBlur?: () => void;
  disabled?: boolean;
  isValid?: boolean;
  value: IDate;
  errorMessage?: string;
  assistiveText?: string;
  validText?: string;
}

export interface IDate {
  day: string | null;
  month: string | null;
  year: string | null;
}

export default function DatePicker({
  id,
  label,
  placeholders = ['DD', 'MM', 'YYYY'],
  subLabels = ['Day', 'Month', 'Year'],
  onChange,
  onBlur,
  disabled,
  isValid,
  value,
  errorMessage,
  assistiveText,
  validText,
}: IDatePicker) {
  function onDayChange(day: string): void {
    onChange({ ...value, day });
  }

  function onMonthChange(month: string): void {
    onChange({ ...value, month });
  }

  function onYearChange(year: string): void {
    onChange({ ...value, year });
  }

  const dateInputProps: IDateInput = {
    id: `${id}-date`,
    label: subLabels[0],
    placeholder: placeholders[0],
    onChange: onDayChange,
    disabled,
    isValid,
    value: value.day ?? '',
  };

  const monthInputProps: IDateInput = {
    id: `${id}-month`,
    label: subLabels[1],
    placeholder: placeholders[1],
    onChange: onMonthChange,
    disabled,
    isValid,
    value: value.month ?? '',
  };

  const yearInputProps: IDateInput = {
    id: `${id}-year`,
    label: subLabels[2],
    placeholder: placeholders[2],
    onChange: onYearChange,
    disabled,
    isValid,
    value: value.year ?? '',
    isWide: true,
    maxLength: 4,
  };

  return (
    <fieldset
      onBlur={(e) => {
        blurHandler(e, onBlur);
      }}
      className={styles.fieldset}
    >
      <legend className={styles.legend}>{label}</legend>
      <div className={styles.inputsContainer} id={id}>
        <div className={styles.inputContainer}>
          <DateInput {...dateInputProps} />
        </div>
        <div className={styles.inputContainer}>
          <DateInput {...monthInputProps} />
        </div>
        <div>
          <DateInput {...yearInputProps} />
        </div>
      </div>
      <ValidationMessage isValid={isValid} errors={[errorMessage]} validText={validText} />
      <AssistiveText>{assistiveText}</AssistiveText>
    </fieldset>
  );
}
